import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { APP_VERSION, AttachmentType, ClockService, Complaint, ComplaintNote, ComplaintPosition, DocumentAttachment, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ComplaintService } from '../complaint.service';
import { ConfigService, core, logger, TranslateService } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { NgxPermissionsService } from 'ngx-permissions';
import { SyncService, getSelectedComplaint } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

@Component({
    selector: 'icc-complaint-note-dialog',
    templateUrl: './complaint-note-dialog.component.html',
    styleUrls: ['./complaint-note-dialog.component.scss'],
})
export class ComplaintNoteDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    /**
     * When true, the dialog is blocked because external status change
     * caused the user to lose note-adding permissions.
     */
    isBlocked = false;

    /**
     * Flag indicating that a status change was detected externally.
     * When true, the next store update will trigger a blocking check.
     */
    private pendingBlockCheck = false;

    /**
     * Timestamp when the dialog was opened.
     * Used to detect external changes that happened after opening.
     */
    private dialogOpenedAt = 0;

    private subscriptions: Subscription[] = [];
    showFullPositionTitles: boolean;
    internalNoteOnly = false;
    privateNoteOnly = false;

    authorizedRetailerDistributorComplaint = false;
    distributorManufacturerComplaint = false;

    complaintNoteAttachmentError: string | null = null;

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint,
            complaintNote: ComplaintNote,
            complaintsPositionsMap: Map<string, ComplaintPosition>,
            attachments: DocumentAttachment[],
            isReply: boolean,
            blockingMode?: boolean,
            customMessage?: string,
        },
        @Inject(APP_VERSION) private appVersion: string,
        private complaintService: ComplaintService,
        private cdr: ChangeDetectorRef,
        private configService: ConfigService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private permissionsService: NgxPermissionsService,
        private syncService: SyncService,
        private store: Store<State>,
    ) {
        this.showFullPositionTitles = this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
        this.authorizedRetailerDistributorComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId !== null;
        this.distributorManufacturerComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId === null;
        this.checkNotePermissions();
    }

    getPositionTitle(position: ComplaintPosition) {
        return this.complaintService.getPositionTitle(position, this.showFullPositionTitles);
    }

    ngOnInit(): void {
        this.dialogOpenedAt = Date.now();
        this.setupExternalChangesDetection();
    }

    ngAfterViewInit() {
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /**
     * Sets up detection of external changes.
     * Blocks the dialog only if an external status change causes the status to no longer allow editing.
     * Note: Adding notes doesn't require assignedTo check - only status matters.
     */
    private setupExternalChangesDetection(): void {
        const statusFields = [
            'distributorStatusId',
            'manufacturerStatusId',
            'authorizedRetailerStatusId',
        ];

        this.subscriptions.push(
            // Subscribe to external changes - set flag when status changes detected
            this.syncService.externalChanges$.subscribe(({
                documentIds,
                documentFields,
                timestamp
            }) => {
                // Ignore changes that happened before dialog was opened
                if (timestamp <= this.dialogOpenedAt) {
                    return;
                }

                // Check if this complaint was modified
                if (!documentIds.has(this.data.complaint.uuid)) {
                    return;
                }

                // If status field changed, check immediately
                if (statusFields.some(field => documentFields.has(field))) {
                    logger.log('[ComplaintNoteDialog] Status field changed externally');
                    this.pendingBlockCheck = true;
                    // Check immediately with current data
                    if (!this.currentStatusAllowEdit()) {
                        logger.log('[ComplaintNoteDialog] BLOCKING - external status change blocks note adding');
                        this.isBlocked = true;
                        this.cdr.detectChanges();
                    }
                }
            }),

            // Subscribe to complaint document changes - update data and check blocking if needed
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && complaint.uuid === this.data.complaint.uuid) {
                    Object.assign(this.data.complaint, complaint);

                    // If we have a pending block check, perform it now with updated data
                    if (this.pendingBlockCheck && !this.isBlocked) {
                        if (!this.currentStatusAllowEdit()) {
                            logger.log('[ComplaintNoteDialog] BLOCKING - external status change blocks note adding');
                            this.isBlocked = true;
                            this.cdr.detectChanges();
                        }
                    }
                }
            }),
        );
    }

    /**
     * Checks if the current status allows editing (adding notes).
     */
    private currentStatusAllowEdit(): boolean {
        const userType = this.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.complaintService.statuses.find((s) => s.id === statusId);
        return status?.allowEdit ?? false;
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    async checkNotePermissions() {
        const hasInternalNotePermission = await this.permissionsService.hasPermission('complaints-add-internal-note');
        const hasPrivateNotePermission = await this.permissionsService.hasPermission('complaints-add-private-note');
        const hasManagePermission = await this.permissionsService.hasPermission('complaints-manage');

        this.internalNoteOnly = hasInternalNotePermission && !hasManagePermission && !hasPrivateNotePermission;
        this.privateNoteOnly = hasPrivateNotePermission && !hasManagePermission && !hasInternalNotePermission;

        if (this.internalNoteOnly) {
            this.data.complaintNote.visibility = 'internal';
        } else if (this.privateNoteOnly) {
            this.data.complaintNote.visibility = 'private';
        }
    }

    async onAddAttachment(files: FileList, type: DocumentAttachment['attachmentType']) {
        const maxSizeInMB = 7;
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        const maxFiles = 5;

        this.complaintNoteAttachmentError = null;

        if (files[0]) {
            if (files[0].size > maxSizeInBytes) {
                this.complaintNoteAttachmentError = this.translateService.instant('COMPLAINT|Plik jest zbyt duży. Maksymalna wielkość to {maxSizeInMB}MB.', { maxSizeInMB });
                (document.getElementById('complaintNoteAttachment' + this.data.complaintNote.uuid) as HTMLInputElement).value = '';
                return;
            }

            if (type === 'complaintNoteAttachment' && this.data.attachments.length >= maxFiles) {
                this.complaintNoteAttachmentError = this.translateService.instant('COMPLAINT|Osiągnięto limit załączników. Możesz dodać maksymalnie {maxFiles} załączników.', { maxFiles });
                (document.getElementById('complaintNoteAttachment' + this.data.complaintNote.uuid) as HTMLInputElement).value = '';
                return;
            }

            const data = await core.blobToArrayBuffer(files[0]);
            const newAttachment: DocumentAttachment = {
                uuid: core.generateUUID(),
                type: 'complaint',
                documentId: this.data.complaint?.uuid,
                complaintNoteId: this.data.complaintNote?.uuid,
                fileType: files[0].type,
                created: new Date(),
                modified: new Date(),
                machineId: ClockService.getClock().timestamp.node(),
                name: files[0].name,
                data,
                attachmentType: type,
                description: '',
            };
            await this.complaintService.cacheAttachment(newAttachment.uuid, files[0]);
            this.data.attachments.push(newAttachment);
        }
    }

    removeAttachment(type: DocumentAttachment['attachmentType'], uuid: string) {
        const attachment = this.data.attachments.find((el) => el.attachmentType === type && el.uuid === uuid);
        if (attachment) {
            (document.getElementById(type + this.data.complaintNote.uuid) as HTMLInputElement).value = '';
            this.data.attachments.splice(this.data.attachments.findIndex(
                (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
            ), 1);
        }
    }

    getAttachments(type: AttachmentType): DocumentAttachment[] {
        return this.data.attachments.filter(
            (attachment) => attachment.attachmentType === type
        );
    }

    getAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        const attachment = this.data.attachments.find(
            (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
        );
        return attachment;
    }

    async save() {
        if (this.data.blockingMode && !this.data.complaintNote.content?.trim()) {
            this.toastService.add({
                severity: 'error',
                title: this.translateService.instant('COMPLAINT|Wymagane jest dodanie treści notatki.'),
                body: '',
            });
            return;
        }
        this.dialogRef.close({
            complaintNote: this.data.complaintNote,
            attachments: this.data.attachments,
        });
    }

    close() {
        this.dialogRef.close();
    }
}
